import React, { Component } from "react";
import * as Icon from "react-feather";

import UtilityService from "../../../../services/UtilityService";
import { SITE_COLUMNS } from "../../../../constants/JaneReports";

import "./journal-entries-panel.css";

class JournalEntriesPanel extends Component {
	formatMoney = ({ amount }) => {
		const isBlankInThisColumn = amount === 0;
		if (isBlankInThisColumn) {
			return "";
		}
		return UtilityService.formatMoney({ value: amount, emptyText: "" });
	};

	renderSiteCell = ({ content }) => {
		const { siteAttribute } = this.props.journal;

		if (!siteAttribute) {
			return null;
		}

		return <span className="journal-entries__line-site tk-text-muted">{content}</span>;
	};

	renderJournalLine = ({ line, index }) => {
		const { siteAttribute } = this.props.journal;
		const hasTaxAmount = Number(line.taxAmount) !== 0;
		const siteName = siteAttribute ? line[SITE_COLUMNS[siteAttribute].nameField] : null;

		return (
			<div className="journal-entries__line" key={index}>
				<span className="journal-entries__line-account">{line.accountName}</span>
				<span className="journal-entries__line-description tk-text-muted">{line.description}</span>
				{this.renderSiteCell({ content: siteName })}
				<span className="journal-entries__line-tax tk-text-muted">{line.taxCodeName}</span>
				<span className="journal-entries__line-tax-amount tk-amount">{hasTaxAmount ? this.formatMoney({ amount: line.taxAmount }) : ""}</span>
				<span className="journal-entries__line-amount tk-amount">{this.formatMoney({ amount: line.debit })}</span>
				<span className="journal-entries__line-amount tk-amount">{this.formatMoney({ amount: line.credit })}</span>
			</div>
		);
	};

	renderAccountGroup = ({ group }) => {
		const hasTax = group.totalTax !== 0;

		return (
			<div className="journal-entries__account-group" key={group.accountId}>
				{group.lines.map((line, index) => this.renderJournalLine({ line, index }))}
				<div className="journal-entries__line journal-entries__line--account-total">
					<span className="journal-entries__line-account" />
					<span className="journal-entries__line-description">Total for {group.accountName}</span>
					{this.renderSiteCell({ content: "" })}
					<span className="journal-entries__line-tax" />
					<span className="journal-entries__line-tax-amount tk-amount">{hasTax ? this.formatMoney({ amount: group.totalTax }) : ""}</span>
					<span className="journal-entries__line-amount tk-amount" />
					<span className="journal-entries__line-amount tk-amount">{this.formatMoney({ amount: group.totalCredits })}</span>
				</div>
			</div>
		);
	};

	renderSectionHead = ({ section }) => (
		<div className="journal-entries__section-head">
			<div className="journal-entries__section-heading">
				<span className="journal-entries__section-title">{section.title}</span>
				<span className="tk-badge tk-badge--neutral">{section.direction}</span>
			</div>
			<div className="journal-entries__section-purpose">{section.purpose}</div>
			<div className="journal-entries__section-source">
				<Icon.FileText size={12} />
				<span>Source: {section.source}</span>
			</div>
		</div>
	);

	renderSection = ({ section, isLastSection }) => {
		const { clearing } = this.props.journal;
		const lineCountText = `${section.lines.length} ${section.lines.length === 1 ? "line" : "lines"}`;
		const clearingAfter = UtilityService.formatMoney({ value: clearing.balanceAfterSection[section.key] });
		const clearingText = isLastSection ? `Left in clearing, waiting for cash: ${clearingAfter}` : `Clearing holds ${clearingAfter} after this`;
		const hasAccountGroups = Boolean(section.accountGroups);

		return (
			<div className="journal-entries__section" key={section.key}>
				{this.renderSectionHead({ section })}
				{hasAccountGroups
					? section.accountGroups.map(group => this.renderAccountGroup({ group }))
					: section.lines.map((line, index) => this.renderJournalLine({ line, index }))}
				<div className="journal-entries__line journal-entries__line--subtotal">
					<span className="journal-entries__line-account">{lineCountText}</span>
					<span className="journal-entries__line-description journal-entries__clearing-after">{clearingText}</span>
					{this.renderSiteCell({ content: "" })}
					<span className="journal-entries__line-tax" />
					<span className="journal-entries__line-tax-amount" />
					<span className="journal-entries__line-amount tk-amount">{this.formatMoney({ amount: section.totalDebits })}</span>
					<span className="journal-entries__line-amount tk-amount">{this.formatMoney({ amount: section.totalCredits })}</span>
				</div>
			</div>
		);
	};

	renderEntry = ({ entry, lastSectionKey }) => {
		const { siteAttribute } = this.props.journal;
		const taxQuickBooksAdds = entry.taxExpected || 0;
		const hasTaxLine = taxQuickBooksAdds !== 0;
		const creditsWithTax = entry.totalCredits + taxQuickBooksAdds;
		const siteHeading = siteAttribute ? SITE_COLUMNS[siteAttribute].heading : null;

		return (
			<div className="journal-entries__entry tk-stacked" key={entry.purpose}>
				<div className="journal-entries__entry-head">
					<span className="journal-entries__entry-title">{entry.title}</span>
					<span className="tk-text-muted journal-entries__entry-date">{entry.date}</span>
				</div>

				<div className="journal-entries__line journal-entries__line--header">
					<span className="journal-entries__line-account">Account</span>
					<span className="journal-entries__line-description">Description</span>
					{this.renderSiteCell({ content: siteHeading })}
					<span className="journal-entries__line-tax">Tax code</span>
					<span className="journal-entries__line-tax-amount">Tax $</span>
					<span className="journal-entries__line-amount">Debit (net)</span>
					<span className="journal-entries__line-amount">Credit (net)</span>
				</div>

				{entry.sections.map(section => this.renderSection({ section, isLastSection: section.key === lastSectionKey }))}

				{hasTaxLine && (
					<div className="journal-entries__line">
						<span className="journal-entries__line-account tk-text-muted">Sales tax payable</span>
						<span className="journal-entries__line-description tk-text-muted">Added by QuickBooks from the tax codes above — not a line of this entry</span>
						{this.renderSiteCell({ content: "" })}
						<span className="journal-entries__line-tax" />
						<span className="journal-entries__line-tax-amount" />
						<span className="journal-entries__line-amount tk-amount" />
						<span className="journal-entries__line-amount tk-amount tk-text-muted">{this.formatMoney({ amount: taxQuickBooksAdds })}</span>
					</div>
				)}

				<div className="journal-entries__line journal-entries__line--total">
					<span className="journal-entries__line-account">{entry.lines.length} lines</span>
					<span className="journal-entries__line-description" />
					{this.renderSiteCell({ content: "" })}
					<span className="journal-entries__line-tax" />
					<span className="journal-entries__line-tax-amount" />
					<span className="journal-entries__line-amount tk-amount">{this.formatMoney({ amount: entry.totalDebits })}</span>
					<span className="journal-entries__line-amount tk-amount">{this.formatMoney({ amount: creditsWithTax })}</span>
				</div>

				{this.renderTaxExplanation({ entry })}
			</div>
		);
	};

	renderTaxExplanation = ({ entry }) => {
		const hasTax = Boolean(entry.taxExpected);

		if (!hasTax) {
			return null;
		}

		return (
			<div className="journal-entries__tax-note">
				<Icon.Info size={13} />
				<span>
					The {this.formatMoney({ amount: entry.taxExpected })} of sales tax is shown so the entry visibly balances, but it is not sent as a line.
					Revenue is credited without the tax and each revenue line carries a tax code, so QuickBooks works the amount owing out itself and it
					reaches the sales tax return. Sending it as a line as well would record the same money twice.
				</span>
			</div>
		);
	};

	render() {
		const { journal } = this.props;
		const shownSections = journal.entries.flatMap(entry => entry.sections);
		const lastSectionKey = shownSections[shownSections.length - 1].key;
		const isReceivableBooked = Boolean(journal.wiring && journal.wiring.receivable);

		return (
			<div className="tk-panel">
				<div className="tk-panel__head">
					<div className="tk-section-label">Journal</div>
					{isReceivableBooked && journal.priorPeriodReceivableSource && (
						<span className="tk-text-muted journal-entries__source">Last month's receivable: {journal.priorPeriodReceivableSource}</span>
					)}
				</div>
				{journal.entries.map(entry => this.renderEntry({ entry, lastSectionKey }))}
				{journal.posting && !journal.posting.isEnabled && (
					<div className="journal-entries__posting tk-text-muted">
						<Icon.Lock size={13} />
						<span>{journal.posting.reason}</span>
					</div>
				)}
			</div>
		);
	}
}

export default JournalEntriesPanel;
